import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@/fontawesome.ts';
import MultiSelectDropdown from '@/components/ui/MultiSelectDropdown.tsx';
import type { EventRecurrenceRule, EventRecurrenceType, EventWeekday, EventWeekOrdinal, RFC3339Date } from '@/models';
import {
  ALL_EVENT_RECURRENCE_TYPES,
  EVENT_RECURRENCE_TYPE_LABELS,
  ALL_EVENT_WEEKDAYS,
  EVENT_WEEKDAY_LABELS,
  ALL_EVENT_WEEK_ORDINALS,
  EVENT_WEEK_ORDINAL_LABELS
} from '@/models';
import { ALLOWED_RECURRENCE_FIELDS, describeRecurrenceRule, normalizeRecurrenceRule } from '@/utils/event-helpers';

const DAYS_OF_MONTH: number[] = Array.from({ length: 31 }, (_, i) => i + 1);

const INTERVAL_UNIT_LABELS: Record<EventRecurrenceType, string> = {
  'WEEKLY':               'Woche(n)',
  'MONTHLY_NTH_WEEKDAY':  'Monat(e)',
  'MONTHLY_DAY_OF_MONTH': 'Monat(e)',
  'YEARLY':               'Jahr(e)',
};

const WEEKDAY_OPTIONS = ALL_EVENT_WEEKDAYS.map(d => ({ value: d, label: EVENT_WEEKDAY_LABELS[d] }));
const ORDINAL_OPTIONS = ALL_EVENT_WEEK_ORDINALS.map(o => ({ value: o, label: EVENT_WEEK_ORDINAL_LABELS[o] }));

export const DEFAULT_RECURRENCE_RULE: EventRecurrenceRule = {
  type: 'WEEKLY',
  interval: 1,
  weekdays: [],
  ordinals: [],
  daysOfMonth: [],
};

interface RecurrenceRuleEditorProps {
  rule: EventRecurrenceRule;
  onChange: (rule: EventRecurrenceRule) => void;
  until: RFC3339Date;
  disabled: boolean;
}

export default function RecurrenceRuleEditor({ rule, onChange, until, disabled }: RecurrenceRuleEditorProps) {
  const allowed = ALLOWED_RECURRENCE_FIELDS[rule.type];

  // The backend rejects fields that don't belong to the selected type, so clear them here
  const handleTypeChange = (type: EventRecurrenceType) => {
    onChange(normalizeRecurrenceRule({ ...rule, type }));
  };

  const toggleDayOfMonth = (day: number) => {
    onChange({
      ...rule,
      daysOfMonth: rule.daysOfMonth.includes(day)
        ? rule.daysOfMonth.filter(d => d !== day)
        : [...rule.daysOfMonth, day]
    });
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Wiederholung *
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Type */}
        <div>
          <label htmlFor="recurrenceType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Rhythmus
          </label>
          <select
            id="recurrenceType"
            value={rule.type}
            onChange={(e) => handleTypeChange(e.target.value as EventRecurrenceType)}
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 cursor-pointer"
          >
            {ALL_EVENT_RECURRENCE_TYPES.map((type) => (
              <option key={type} value={type}>
                {EVENT_RECURRENCE_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>

        {/* Interval */}
        <div>
          <label htmlFor="recurrenceInterval" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Intervall
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">alle</span>
            <input
              type="number"
              id="recurrenceInterval"
              min={1}
              max={52}
              value={rule.interval}
              onChange={(e) => onChange({ ...rule, interval: Number(e.target.value) })}
              disabled={disabled}
              className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">{INTERVAL_UNIT_LABELS[rule.type]}</span>
          </div>
        </div>
      </div>

      {/* Ordinals - MONTHLY_NTH_WEEKDAY only */}
      {allowed.ordinals && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Vorkommen im Monat *
          </label>
          {/* MultiSelectDropdown has no disabled prop, so gate it from the outside */}
          <div className={disabled ? 'opacity-50 pointer-events-none' : ''} aria-disabled={disabled}>
            <MultiSelectDropdown<EventWeekOrdinal>
              options={ORDINAL_OPTIONS}
              selected={rule.ordinals}
              onChange={(ordinals) => onChange({ ...rule, ordinals })}
              placeholder="Bitte auswählen"
              selectedCountSuffix="ausgewählt"
              dropdownClassName="recurrence-ordinals-dropdown"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            „Letzter" trifft je nach Monat das 4. oder 5. Vorkommen.
          </p>
        </div>
      )}

      {/* Weekdays - WEEKLY + MONTHLY_NTH_WEEKDAY */}
      {allowed.weekdays && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Wochentage *
          </label>
          <div className={disabled ? 'opacity-50 pointer-events-none' : ''} aria-disabled={disabled}>
            <MultiSelectDropdown<EventWeekday>
              options={WEEKDAY_OPTIONS}
              selected={rule.weekdays}
              onChange={(weekdays) => onChange({ ...rule, weekdays })}
              placeholder="Bitte auswählen"
              selectedCountSuffix="ausgewählt"
              dropdownClassName="recurrence-weekdays-dropdown"
            />
          </div>
          {rule.type === 'MONTHLY_NTH_WEEKDAY' && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Vorkommen und Wochentage werden kombiniert — „Erster + Zweiter" mit „Montag + Mittwoch" ergibt vier Termine pro Monat.
            </p>
          )}
        </div>
      )}

      {/* Days of month - MONTHLY_DAY_OF_MONTH only */}
      {allowed.daysOfMonth && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tage im Monat *
          </label>
          <div className="grid grid-cols-7 gap-1.5">
            {DAYS_OF_MONTH.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => toggleDayOfMonth(day)}
                disabled={disabled}
                className={`py-1.5 text-sm rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
                  rule.daysOfMonth.includes(day)
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {day}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            In kürzeren Monaten wird auf den letzten Tag des Monats gekürzt (der 31. wird im Februar zum 28./29.).
          </p>
        </div>
      )}

      {rule.type === 'YEARLY' && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Jährliche Serien nutzen Monat und Tag des Serienstarts — es ist keine weitere Auswahl nötig.
        </p>
      )}

      {/* Plain-text summary */}
      <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
        <div className="flex items-start">
          <FontAwesomeIcon icon={faCircleInfo} className="w-4 h-4 mr-2 mt-0.5 text-gray-400 dark:text-gray-500 shrink-0" />
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {describeRecurrenceRule(rule, until)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Alle Termine werden in der Zeitzone Europe/Berlin erzeugt, die Uhrzeit bleibt über die Sommer-/Winterzeit hinweg gleich. Maximal 500 Termine pro Serie.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
